import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { apiErrorMessage, useAuth } from "@/AuthContext";
import { LOGOUT } from "@/constants/testIds/auth";
import AdminSupportSection from "@/pages/AdminSupportSection";
import AdminNav from "@/pages/AdminNav";

function paidSubscriptionLabel(u) {
  const paid =
    u.license_status === "active" &&
    Boolean(u.paid_plan || u.billing_provider === "paddle" || u.paddle_subscription_id);
  if (!paid) return null;
  const plan = u.paid_plan || "Basic";
  let label = `Paid • ${plan}`;
  if (u.subscription_current_period_end) {
    const d = new Date(u.subscription_current_period_end);
    if (!Number.isNaN(d.getTime())) {
      label += ` · ${d.toLocaleDateString()}`;
    }
  }
  return label;
}

function trialColumnLabel(u) {
  const paid = paidSubscriptionLabel(u);
  if (paid) return paid;
  if (u.trial_status === "active") return `${u.trial_days_remaining ?? 0}d left`;
  if (u.trial_status === "expired") return "ended";
  return "—";
}

export default function AdminPage() {
  const { logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  async function load() {
    const res = await api.get("/admin/users");
    setUsers(res.data || []);
  }

  useEffect(() => {
    load().catch((err) => setError(apiErrorMessage(err)));
  }, []);

  const counts = useMemo(() => {
    const total = users.length;
    const active = users.filter((u) => u.license_status === "active").length;
    return { total, active, inactive: total - active };
  }, [users]);

  async function setLicense(userId, license_status) {
    setBusyId(userId);
    setError("");
    try {
      await api.patch(`/admin/users/${userId}/license`, { license_status });
      await load();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="mp-shell mp-wide">
      <header className="mp-top">
        <div>
          <p className="mp-kicker">MinutePay</p>
          <h1>Admin</h1>
        </div>
        <div className="mp-top-actions">
          <Button variant="outline" asChild>
            <Link to="/dashboard">My license</Link>
          </Button>
          <Button data-testid={LOGOUT.button} variant="outline" onClick={logout}>
            Log out
          </Button>
        </div>
      </header>

      <AdminNav />

      <section className="mp-counts">
        <article><span>Total</span><strong>{counts.total}</strong></article>
        <article><span>Active</span><strong>{counts.active}</strong></article>
        <article><span>Inactive</span><strong>{counts.inactive}</strong></article>
      </section>

      {error ? <p className="mp-error">{error}</p> : null}

      <div className="mp-table-wrap">
        <table className="mp-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>License</th>
              <th>Access</th>
              <th>Trial</th>
              <th>Trial ends</th>
              <th>Stage</th>
              <th>Last email</th>
              <th>Review</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.email}</td>
                <td>{u.role}</td>
                <td>
                  <span className={`mp-pill ${u.license_status === "active" ? "on" : "off"}`}>
                    {u.license_status}
                  </span>
                </td>
                <td>
                  <span className={`mp-pill ${u.has_access ? "on" : "off"}`}>
                    {u.is_blocked ? "blocked" : u.has_access ? "on" : "off"}
                  </span>
                </td>
                <td>{trialColumnLabel(u)}</td>
                <td>{u.trial_ends_at ? new Date(u.trial_ends_at).toLocaleDateString() : "—"}</td>
                <td>{u.lifecycle_stage || "—"}</td>
                <td>
                  {u.last_lifecycle_email_type
                    ? `${u.last_lifecycle_email_type}${u.last_lifecycle_email_at ? ` · ${new Date(u.last_lifecycle_email_at).toLocaleDateString()}` : ""}`
                    : "—"}
                </td>
                <td>{u.review_request_sent ? "yes" : "no"}</td>
                <td>{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                <td>
                  {u.role === "admin" ? (
                    <span className="mp-pill off">Owner</span>
                  ) : u.has_access ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busyId === u.id}
                      onClick={() => setLicense(u.id, "inactive")}
                    >
                      Deactivate
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      disabled={busyId === u.id}
                      onClick={() => setLicense(u.id, "active")}
                    >
                      Activate
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 ? <p className="mp-empty">No users yet.</p> : null}
      </div>

      <AdminSupportSection />
    </div>
  );
}
